import React, { useEffect, useLayoutEffect, useState } from 'react';
import type { FC } from 'react';

import { Flex } from '@atlaskit/primitives/compiled';
import { useExpandSideNav } from '@atlaskit/navigation-system/layout/side-nav';
import { UNSAFE_useMediaQuery as useMediaQuery } from '@atlaskit/primitives/responsive';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSSRPlaceholderReplaceIdProp, LoadableAfterPaint } from '@confluence/loadable';
import { ContextualCreateContentPopoverLoader } from '@confluence/create-blank-fabric-page/entry-points/ContextualCreateContentPopover.ts';
import { useIsCreationDropdownEnabled } from '@confluence/content-types-utils/entry-points/useIsCreationDropdownEnabled.ts';
import { useIsMultiSelectActive } from '@confluence/content-tree-actions/entry-points/useIsMultiSelectActive.ts';
import {
	MultiSelectMode,
	useContentTreeSelection,
} from '@confluence/content-tree-actions/entry-points/store.ts';
import { expVal } from '@confluence/feature-experiments';

import type { SetSelection } from './SpaceViewsMenu.tsx';
import { MultiSelectButton, ExitMultiSelectButton } from './MultiSelectButton.tsx';

const SpaceViewsMenuLoader = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-SpaceViewsMenu" */ './SpaceViewsMenu.tsx'))
			.SpaceViewsMenu,
});

type PagesItemActionsProps = {
	isLicensed: boolean;
	edition?: string;
	selection: string;
	setSelection: SetSelection;
	spaceKey: string;
	expandContentSection?: () => void;
	onItemClick?: () => void;
	spaceHasUserContent: boolean;
	pageTreeFinishedLoading: boolean;
	isSpaceAdminOrSuperAdmin: boolean;
	spaceViewsMenuTriggerRef?: React.RefObject<HTMLButtonElement>;
};

export const PagesItemActions: FC<PagesItemActionsProps> = ({
	isLicensed,
	edition,
	selection,
	setSelection,
	spaceKey,
	expandContentSection,
	onItemClick,
	spaceHasUserContent,
	pageTreeFinishedLoading,
	isSpaceAdminOrSuperAdmin,
	spaceViewsMenuTriggerRef,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const canShowFolder = selection === 'TREE';
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const isCreationADropdown = useIsCreationDropdownEnabled();
	const [isContextualCreateFocused, setIsContextualCreateFocused] = useState<boolean>(false);
	const [isContextualCreatePopupOpen, setIsContextualCreatePopupOpen] = useState<boolean>(false);
	const [{ multiSelectMode }, { enterMultiSelect, exitMultiSelect }] = useContentTreeSelection();
	const [isMultiSelectActive] = useIsMultiSelectActive();
	const [isMobile, setIsMobile] = useState<boolean>(false);
	const mediaQuery = useMediaQuery('below.md', function onChange(event) {
		// shifting between mobile and desktop
		if (isMultiSelectActive) {
			exitMultiSelect();
		}
		setIsMobile(event.matches);
	});

	useLayoutEffect(() => {
		setIsMobile(mediaQuery?.matches ?? false);
	}, [mediaQuery]);

	const isMultiSelectEnabled =
		isSpaceAdminOrSuperAdmin &&
		edition === 'PREMIUM' &&
		expVal('confluence_bulk_actions_in_content_tree', 'isEnabled', false);

	const expandSideNav = useExpandSideNav();
	useEffect(() => {
		return () => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: 'contentTreeBulkActions',
					action: 'closed',
					actionSubject: 'bulkActionsMenu',
					attributes: {
						method: 'unmount',
					},
				},
			}).fire();
			exitMultiSelect();
		};
	}, [createAnalyticsEvent, exitMultiSelect]);

	const spaceViewsMenu = isLicensed ? (
		<SpaceViewsMenuLoader
			selection={selection}
			setSelection={setSelection}
			isWhiteboardBetaEnabled={isCreationADropdown}
			edition={edition}
			isSpaceAdminOrSuperAdmin={isSpaceAdminOrSuperAdmin}
			triggerRef={spaceViewsMenuTriggerRef}
		/>
	) : null;

	const contextualCreate = (
		<>
			<ContextualCreateContentPopoverLoader
				isContextualCreateFocused={isContextualCreateFocused}
				setIsContextualCreateFocused={setIsContextualCreateFocused}
				shouldShowPopup={isContextualCreatePopupOpen}
				setShouldShowPopup={setIsContextualCreatePopupOpen}
				spaceKey={spaceKey}
				source="spaceLevelContextualCreate"
				rootCreateButtonStyling
				onItemClick={onItemClick}
				shouldRenderToParent
				hideFolder={!canShowFolder}
			/>
		</>
	);

	const onMultiSelectClick = () => {
		expandSideNav();
		expandContentSection?.();
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: 'contentTreeBulkActions',
				action: 'opened',
				actionSubject: 'bulkActionsMenu',
				actionSubjectId: 'multiSelectButton',
			},
		}).fire();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'contentTreeBulkActions',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'multiSelectButton',
				attributes: {
					spaceKey,
				},
			},
		}).fire();
		enterMultiSelect();
	};

	const onExitMultiSelectClick = () => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: 'contentTreeBulkActions',
				action: 'closed',
				actionSubject: 'bulkActionsMenu',
				attributes: {
					method: 'multiSelectExitButton',
				},
			},
		}).fire();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'contentTreeBulkActions',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'multiSelectExitButton',
			},
		}).fire();
		exitMultiSelect();
	};

	const nav4Actions = isMultiSelectActive ? (
		<ExitMultiSelectButton onClick={onExitMultiSelectClick} />
	) : (
		<>
			{isMultiSelectEnabled && (
				<MultiSelectButton
					onClick={onMultiSelectClick}
					isDisabled={multiSelectMode === MultiSelectMode.DISABLED}
					isLoading={!pageTreeFinishedLoading}
					spaceHasUserContent={spaceHasUserContent}
					isMultiSelectEnabled={isMultiSelectEnabled}
					isMobile={isMobile}
					spaceKey={spaceKey}
				/>
			)}
			{contextualCreate}
			{!isMultiSelectEnabled && spaceViewsMenu}
		</>
	);

	return (
		<Flex data-vc="pages-item-actions" {...ssrPlaceholderIdProp}>
			<>{nav4Actions}</>
		</Flex>
	);
};
